// ============================================================================
// Shared building blocks for the real-data tabs (Orders, POs): the data loader, summary cell,
// section panel, Stuck-by-stage table and the team tables. One copy, so every tab looks and
// behaves the same. A `null` number means "rule not defined yet" and shows "–" (O15).
// ============================================================================
import { Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { REFRESH_INTERVAL_MS } from "../../config/rules";
import { STATUS_META } from "../../format";
import {
  OcEscalationSummary,
  OcEscalationTeamRow,
  OcOrdersStage,
  OcScopeParams,
  OcSlaAtRisk,
  OcSlaBreached,
  OcStageBreakdown,
  OcTechErrorSummary,
} from "../../types/orders";
import MetricLink from "../metric_link";
import styles from "../../index.module.scss";

export const DASH = <span className={styles.metricZero}>–</span>;

// The last numbers per tab + scope, so going back to a tab (or a scope) shows them at once while a
// background refresh runs, instead of a skeleton. Lives as long as the page's module.
const overviewCache = new Map<string, { data: unknown; at: Date }>();
const OVERVIEW_CACHE_MAX = 30; // oldest dropped first
export const clearOverviewCache = () => overviewCache.clear();

// Loads a tab's overview for the scope, refreshes every REFRESH_INTERVAL_MS while the browser tab is
// visible, keeps the last good numbers on screen while a refresh is in flight.
export const useTabOverview = <T,>(
  fetcher: (s: OcScopeParams) => Promise<T>,
  scope: OcScopeParams,
  what: string,
) => {
  const key = JSON.stringify(scope);
  const cacheKey = `${what}|${key}`;
  const [data, setData] = useState<T | null>(() => (overviewCache.get(cacheKey)?.data as T) ?? null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(() => overviewCache.get(cacheKey)?.at ?? null);
  const seq = useRef(0);
  const inFlight = useRef(false);
  const lastLoad = useRef(0);

  const load = useCallback(
    async (clear: boolean) => {
      const mine = ++seq.current;
      if (clear) {
        // A new scope: its own last numbers if we have them, else the skeleton.
        const hit = overviewCache.get(cacheKey);
        setData((hit?.data as T) ?? null);
        if (hit) setUpdatedAt(hit.at);
      }
      setLoading(true);
      setError(null);
      inFlight.current = true;
      lastLoad.current = Date.now();
      try {
        const res = await fetcher(scope);
        if (mine !== seq.current) return;
        const at = new Date();
        overviewCache.delete(cacheKey);
        overviewCache.set(cacheKey, { data: res, at });
        if (overviewCache.size > OVERVIEW_CACHE_MAX) overviewCache.delete(overviewCache.keys().next().value as string);
        setData(res);
        setUpdatedAt(at);
      } catch (e: any) {
        if (mine === seq.current)
          setError(
            e?.response?.data?.detail ??
              e?.message ??
              `Could not load the ${what} tab`,
          );
      } finally {
        if (mine === seq.current) {
          setLoading(false);
          inFlight.current = false;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheKey],
  );

  useEffect(() => {
    load(true);
    // No polling while the browser tab is hidden or the previous request is still running; one
    // refresh on coming back if the numbers are older than the interval.
    const tick = () => {
      if (document.visibilityState === "visible" && !inFlight.current) load(false);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastLoad.current >= REFRESH_INTERVAL_MS) tick();
    };
    const t = setInterval(tick, REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- a counter, not a DOM ref
      seq.current++; // drop the answer of a request still in flight for the old scope
      inFlight.current = false;
    };
  }, [load]);

  return { data, error, loading, updatedAt, refresh: () => load(false) };
};

// Summary-block accent, from the page's status palette (format.ts STATUS_META) plus the brand teal:
// brand = volume totals, info = what is coming next, good = done, warn = needs attention,
// bad = breached, violet = undelivered. Status tones also colour the number (zeros stay grey).
export type Tone = "brand" | "info" | "good" | "warn" | "bad" | "violet";

const TONE_CLASS: Record<Tone, string> = {
  brand: styles.toneBrand,
  info: styles.toneInfo,
  good: styles.toneGood,
  warn: styles.toneWarn,
  bad: styles.toneBad,
  violet: styles.toneViolet,
};

export const Cell: React.FC<{
  label: string;
  children: React.ReactNode;
  sub?: React.ReactNode;
  tone?: Tone;
}> = ({ label, children, sub, tone }) => (
  <div
    className={`${styles.stripCell} ${tone ? `${styles.toned} ${TONE_CLASS[tone]}` : ""}`}
  >
    <div className={styles.stripLabel}>{label}</div>
    <div className={styles.stripValue}>{children}</div>
    <div className={styles.stripSub}>{sub ?? " "}</div>
  </div>
);

export const Section: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <section className={styles.panel}>
    <div className={styles.panelHead}>
      <h3 className={styles.panelTitle}>{title}</h3>
    </div>
    {children}
  </section>
);

// "today" / "tomorrow" / "yesterday", else "· 5 Oct" / "· 1 Oct – 5 Oct", for block labels.
export const whenLabel = (rangeLabel: string) =>
  rangeLabel === "Today" ||
  rangeLabel === "Tomorrow" ||
  rangeLabel === "Yesterday"
    ? rangeLabel.toLowerCase()
    : `· ${rangeLabel}`;

// ---- Teams ------------------------------------------------------------------------------------

export interface TeamRow {
  team: string;
  stages: string;
}

// Teams from the stage owners the backend sends (an owner of "–" is not a team), plus extras
// such as Customer success, which owns an escalation category but no stage.
export const teamsFrom = (
  stages: OcOrdersStage[],
  extra: TeamRow[] = [],
): TeamRow[] => {
  const byOwner = new Map<string, string[]>();
  stages
    .filter((s) => s.owner && s.owner !== "–")
    .forEach((s) =>
      byOwner.set(s.owner, [...(byOwner.get(s.owner) ?? []), s.label]),
    );
  return [
    ...Array.from(byOwner.entries()).map(([team, labels]) => ({
      team,
      stages: labels.join(", "),
    })),
    ...extra,
  ];
};

export const TeamTable: React.FC<{
  teams: TeamRow[];
  columns: string[];
  first?: string;
}> = ({ teams, columns, first = "Team" }) => {
  const cols: ColumnsType<TeamRow> = [
    {
      title: first,
      dataIndex: "team",
      render: (v: string) => <span className={styles.strong}>{v}</span>,
    },
    {
      title: "Stages owned",
      dataIndex: "stages",
      render: (v: string) => <span className={styles.muted}>{v}</span>,
    },
    ...columns.map((c) => ({
      title: c,
      key: c,
      align: "right" as const,
      width: 120,
      render: () => DASH,
    })),
  ];
  return (
    <Table<TeamRow>
      size="small"
      rowKey="team"
      columns={cols}
      dataSource={teams}
      pagination={false}
      className={styles.denseTable}
      scroll={{ x: 1000 }}
    />
  );
};

// A column header with its status colour as a small dot.
export const ColHead: React.FC<{
  color: string;
  children: React.ReactNode;
}> = ({ color, children }) => (
  <span className={styles.colHead}>
    <span className={styles.dot} style={{ background: color }} />
    {children}
  </span>
);

// A column header with a hover explanation (dotted underline = "hover me").
export const HelpHead: React.FC<{ label: string; help: string }> = ({
  label,
  help,
}) => (
  <Tooltip title={help}>
    <span className={styles.helpHead}>{label}</span>
  </Tooltip>
);

// Done %: green from 80, amber from 50, red below (user, Q5).
export const PctText: React.FC<{
  value: number | null | undefined;
  suffix?: string;
}> = ({ value, suffix = "" }) =>
  value == null ? (
    DASH
  ) : (
    <span
      className={styles.pct}
      style={{
        color:
          value >= 80
            ? STATUS_META.done_on_time.text
            : value >= 50
              ? STATUS_META.at_risk_minor.text
              : STATUS_META.breached.text,
      }}
    >
      {value}%{suffix}
    </span>
  );

// The "info" blue of the summary blocks: what is coming next.
const NEXT_DAY_COLOR = "#2f54eb";

// Hours as "45 min" / "5.2 h" / "3.1 d".
export const fmtHours = (h: number | null | undefined): string => {
  if (h == null) return "–";
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 48) return `${Math.round(h * 10) / 10} h`;
  return `${Math.round((h / 24) * 10) / 10} d`;
};

// One line of a block's sub-line: its parts joined by " · ". A part never breaks inside ("avg 7 h"
// stays whole); a long line wraps only between parts. Empty parts are skipped.
export const SubLine: React.FC<{ parts: React.ReactNode[] }> = ({ parts }) => {
  const shown = parts.filter((p) => p !== null && p !== undefined && p !== false && p !== "");
  if (!shown.length) return null;
  return (
    <div>
      {shown.map((p, i) => (
        <React.Fragment key={i}>
          {i ? " · " : null}
          <span className={styles.subPart}>{p}</span>
        </React.Fragment>
      ))}
    </div>
  );
};

// ---- Summary blocks shared by every tab: Open escalations, Tech errors ---------------------------

export const OpenEscalationsCell: React.FC<{
  esc: OcEscalationSummary;
  onDrill: (title: string, metric: string) => void;
}> = ({ esc, onDrill }) => (
  <Cell
    tone="warn"
    label="Open escalations"
    sub={
      <SubLine
        parts={[
          <MetricLink
            value={`${esc.open_long} over 24h`}
            count={esc.open_long}
            color={STATUS_META.breached.text}
            onClick={() =>
              onDrill("Escalations open over 24h", "escalations_open_long")
            }
          />,
          <MetricLink
            value={`${esc.resolved} resolved`}
            count={esc.resolved}
            onClick={() =>
              onDrill("Escalations resolved", "escalations_resolved")
            }
          />,
          esc.avg_resolve_h != null ? `avg ${fmtHours(esc.avg_resolve_h)}` : null,
        ]}
      />
    }
  >
    <Tooltip
      title={`${esc.open} open escalations on ${esc.open_units} POs / shipments (a shipment's escalation covers every PO on it)`}
    >
      <span>
        <MetricLink
          value={esc.open}
          count={esc.open}
          onClick={() => onDrill("Open escalations", "escalations_open")}
        />
      </span>
    </Tooltip>
  </Cell>
);

// SLA summary blocks (the SLA settings tab's rules; units on the stages below). On the Orders tab the
// main number is orders and the shipments sit under it (user, 2026-10-07: show both).
const Count: React.FC<{
  n: number;
  unit?: string;
  color?: string;
  onClick: () => void;
}> = ({ n, unit, color, onClick }) => (
  <span>
    <MetricLink value={n} count={n} color={color} onClick={onClick} />
    {unit ? <span className={styles.blockUnit}> {unit}</span> : null}
  </span>
);

export const AtRiskCell: React.FC<{
  v: OcSlaAtRisk | null;
  onDrill: (title: string, metric: string) => void;
  label?: string;
  cartNote?: string; // after the cart-line count (POs tab: "all ECs")
}> = ({ v, onDrill, label = "At risk", cartNote }) => {
  const byOrder = v?.orders != null;
  return (
    <Cell
      tone="warn"
      label={label}
      sub={
        v ? (
          <>
            {/* Line 1: what the main number is made of (every part is inside it). */}
            <SubLine
              parts={[
                byOrder ? (
                  <MetricLink
                    value={`${v.count} ${v.unit ?? ""}`.trim()}
                    count={v.count}
                    onClick={() =>
                      onDrill("At risk (SLA) · shipments", "sla_at_risk")
                    }
                  />
                ) : null,
                `${v.major} major`,
                `${v.minor} minor`,
                v.cart != null ? (
                  <MetricLink
                    value={`${v.cart} cart lines${cartNote ?? ""}`}
                    count={v.cart}
                    onClick={() =>
                      onDrill("Cart lines at risk (SLA)", "sla_at_risk_cart")
                    }
                  />
                ) : null,
                v.cart == null && v.escalated ? (
                  <MetricLink
                    value={`${v.escalated} escalated`}
                    count={v.escalated}
                    title="Still at risk, with a Delivery-team escalation on it: that does not excuse it (it is the step's own problem)"
                    onClick={() => onDrill("At risk · escalated to Delivery", "sla_at_risk_escalated")}
                  />
                ) : null,
              ]}
            />
            {/* Line 2: outside the main number, as on Breached (user, 2026-10-12). */}
            <SubLine
              parts={[
                <MetricLink
                  value={`+${byOrder ? (v.excused_orders ?? 0) : (v.excused ?? 0)} excused by escalation`}
                  count={byOrder ? (v.excused_orders ?? 0) : (v.excused ?? 0)}
                  title="An escalation on that step (not a Delivery-team one) excuses the risk, as it does a breach"
                  onClick={() =>
                    onDrill(
                      "At risk excused by an escalation",
                      byOrder ? "sla_at_risk_excused_orders" : "sla_at_risk_excused",
                    )
                  }
                />,
              ]}
            />
          </>
        ) : (
          "rule pending"
        )
      }
    >
      {!v ? (
        DASH
      ) : byOrder ? (
        <Count
          n={v.orders ?? 0}
          unit="orders"
          onClick={() =>
            onDrill("At risk (SLA) · orders", "sla_at_risk_orders")
          }
        />
      ) : (
        <Count
          n={v.count}
          unit={v.unit}
          onClick={() => onDrill("At risk (SLA)", "sla_at_risk")}
        />
      )}
    </Cell>
  );
};

export const BreachedCell: React.FC<{
  v: OcSlaBreached | null;
  onDrill: (title: string, metric: string) => void;
  label?: string;
  cartNote?: string; // after the cart-line count (POs tab: "all ECs")
}> = ({ v, onDrill, label = "Breached", cartNote }) => {
  const byOrder = v?.orders != null;
  return (
    <Cell
      tone="bad"
      label={label}
      sub={
        v ? (
          <>
            {byOrder ? (
              <>
                <MetricLink
                  value={`${v.count} ${v.unit ?? ""}`.trim()}
                  count={v.count}
                  onClick={() =>
                    onDrill("Breached (SLA) · shipments", "sla_breached")
                  }
                />
                {" · "}
              </>
            ) : null}
            {v.cart != null ? (
              <>
                <MetricLink
                  value={`${v.cart} cart lines${cartNote ?? ""}`}
                  count={v.cart}
                  color={STATUS_META.breached.text}
                  onClick={() =>
                    onDrill("Cart lines breached (SLA)", "sla_breached_cart")
                  }
                />
                {" · "}
              </>
            ) : null}
            <MetricLink
              value={`+${byOrder ? (v.excused_orders ?? 0) : v.excused} excused by escalation`}
              count={byOrder ? (v.excused_orders ?? 0) : v.excused}
              onClick={() =>
                onDrill(
                  "Breach excused by an escalation",
                  byOrder ? "sla_excused_orders" : "sla_excused",
                )
              }
            />
          </>
        ) : (
          "rule pending"
        )
      }
    >
      {!v ? (
        DASH
      ) : byOrder ? (
        <Count
          n={v.orders ?? 0}
          unit="orders"
          color={STATUS_META.breached.text}
          onClick={() =>
            onDrill("Breached (SLA) · orders", "sla_breached_orders")
          }
        />
      ) : (
        <Count
          n={v.count}
          unit={v.unit}
          color={STATUS_META.breached.text}
          onClick={() => onDrill("Breached (SLA)", "sla_breached")}
        />
      )}
    </Cell>
  );
};

export const TechErrorsCell: React.FC<{
  tech: OcTechErrorSummary;
  onDrill: (title: string, metric: string) => void;
}> = ({ tech, onDrill }) => (
  <Cell
    tone="bad"
    label="Tech errors"
    sub={
      <>
        <MetricLink
          value={`+${tech.older} older`}
          count={tech.older}
          onClick={() =>
            onDrill("Tech errors · outside this range", "tech_errors_older")
          }
        />
        {tech.oldest_h != null ? ` · oldest ${fmtHours(tech.oldest_h)}` : ""}
      </>
    }
  >
    <MetricLink
      value={tech.count}
      count={tech.count}
      onClick={() => onDrill("Tech errors", "tech_errors")}
    />
  </Cell>
);

// ---- Escalations · by team (the OMS team that must resolve it, E14) + Tech ------------------------

export const EscalationTeamsTable: React.FC<{
  rows: OcEscalationTeamRow[];
  onDrill: (title: string, metric: string) => void;
}> = ({ rows, onDrill }) => {
  const link =
    (part: "raised" | "open" | "open_long", label: string, color?: string) =>
    (v: number, r: OcEscalationTeamRow) => (
      <MetricLink
        value={v}
        count={v}
        color={color}
        onClick={() =>
          onDrill(`${r.label} · ${label}`, `esc_team:${r.team}:${part}`)
        }
      />
    );
  const cols: ColumnsType<OcEscalationTeamRow> = [
    {
      title: "Team (resolves it)",
      dataIndex: "label",
      render: (v: string, r) => (
        <span className={styles.strong}>
          {v}
          {r.team === "tech" ? (
            <span className={styles.mutedSmall}> · tech errors</span>
          ) : null}
        </span>
      ),
    },
    {
      title: (
        <Tooltip title="The team's 3 most frequent issue types (Tech: error types). An escalation with two types counts for both.">
          Top issues
        </Tooltip>
      ),
      key: "top_issues",
      render: (_: unknown, r) =>
        r.top_issues?.length ? (
          <SubLine
            parts={r.top_issues.map((it) => (
              <MetricLink
                value={`${it.label} ×${it.count}`}
                count={it.count}
                onClick={() => onDrill(`${r.label} · ${it.label}`, it.metric)}
              />
            ))}
          />
        ) : (
          DASH
        ),
    },
    {
      title: "Raised",
      dataIndex: "raised",
      align: "right",
      width: 110,
      render: link("raised", "escalations"),
    },
    {
      title: "Open",
      dataIndex: "open",
      align: "right",
      width: 110,
      render: link("open", "open", STATUS_META.at_risk_major.text),
    },
    {
      title: "Open > 24h",
      dataIndex: "open_long",
      align: "right",
      width: 120,
      render: link("open_long", "open over 24h", STATUS_META.breached.text),
    },
    {
      title: "Avg time to resolve",
      dataIndex: "avg_resolve_h",
      align: "right",
      width: 160,
      render: (v: number | null) => (v == null ? DASH : fmtHours(v)),
    },
  ];
  return (
    <Table<OcEscalationTeamRow>
      size="small"
      rowKey="team"
      columns={cols}
      dataSource={rows}
      pagination={false}
      className={styles.denseTable}
      scroll={{ x: 900 }}
    />
  );
};

// ---- Stuck by stage ------------------------------------------------------------------------------

// A stage's SLA in one cell: a thin bar (on time / minor / major / breached / excused) and the counts.
const SlaSummary: React.FC<{
  stage: OcOrdersStage;
  onDrill: (title: string, metric: string) => void;
}> = ({ stage, onDrill }) => {
  if (stage.breached == null) return DASH;
  const parts = [
    {
      key: "on_track",
      label: "on time",
      n: stage.on_track ?? 0,
      color: STATUS_META.done_on_time.color,
      text: STATUS_META.done_on_time.text,
    },
    {
      key: "minor",
      label: "minor",
      n: stage.minor ?? 0,
      color: STATUS_META.at_risk_minor.color,
      text: STATUS_META.at_risk_minor.text,
    },
    {
      key: "major",
      label: "major",
      n: stage.major ?? 0,
      color: STATUS_META.at_risk_major.color,
      text: STATUS_META.at_risk_major.text,
    },
    {
      key: "breached",
      label: "breached",
      n: stage.breached ?? 0,
      color: STATUS_META.breached.color,
      text: STATUS_META.breached.text,
    },
    {
      key: "excused",
      label: "excused",
      n: stage.excused ?? 0,
      color: "#bfbfbf",
      text: "#595959",
    },
  ];
  const total = parts.reduce((t, p) => t + p.n, 0);
  const drill = (key: string, label: string) => () =>
    onDrill(`${stage.label} · ${label}`, `stage:${stage.key}:${key}`);
  return (
    <div className={styles.slaCell}>
      <div className={styles.slaBar}>
        {total
          ? parts
              .filter((p) => p.n)
              .map((p) => (
                <span
                  key={p.key}
                  style={{ flex: p.n, background: p.color }}
                  title={`${p.n} ${p.label}`}
                />
              ))
          : null}
      </div>
      <div className={styles.slaCounts}>
        {parts.map((p, i) => (
          <React.Fragment key={p.key}>
            {i ? <span className={styles.mutedSmall}> · </span> : null}
            <MetricLink
              value={`${p.n} ${p.label}`}
              count={p.n}
              color={p.text}
              onClick={drill(p.key, p.label)}
            />
            {p.key === "breached" &&
            (stage.breached_slight || stage.breached_very) ? (
              <span className={styles.mutedSmall}>
                {" ("}
                <MetricLink
                  value={`${stage.breached_slight ?? 0} slightly`}
                  count={stage.breached_slight ?? 0}
                  onClick={drill("breached_slight", "breached, slightly late")}
                />
                {" / "}
                <MetricLink
                  value={`${stage.breached_very ?? 0} very late`}
                  count={stage.breached_very ?? 0}
                  onClick={drill("breached_very", "breached, very late")}
                />
                {")"}
              </span>
            ) : null}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const SLA_HELP =
  "On time / Minor / Major / Breached against the SLA settings. Breached splits into slightly and very late; excused = a non-Delivery escalation raised before the breach time. Where the Major line is after the breach line (Cart, PO raise, Picklist others), very late breaches also count as Major.";

// Shared by both stage tables (full and compact), so their drills, SLA column and expanded row
// cannot diverge (review, PR #507).
type Drill = (title: string, metric: string) => void;

const stageDrill = (onDrill: Drill) => (r: OcOrdersStage, part: string, label: string) => () =>
  onDrill(`${r.label} · ${label}`, `stage:${r.key}:${part}`);

const slaColumn = (onDrill: Drill) => ({
  title: <HelpHead label="SLA" help={SLA_HELP} />,
  key: "sla",
  width: 430,
  render: (_: unknown, r: OcOrdersStage) => <SlaSummary stage={r} onDrill={onDrill} />,
});

// A stage with exclusions always expands (the compact table shows Excluded only there).
const stageExpandable = (onDrill: Drill) => ({
  rowExpandable: (r: OcOrdersStage) => !!r.breakdown?.length || !!r.excluded,
  expandedRowRender: (r: OcOrdersStage) => <StageBreakdown stage={r} onDrill={onDrill} />,
});

// The Shipments tab's stage table: 8 columns instead of 13 (see StagesTable `compact`). Laid out as a
// number table (user, 2026-10-20): the stage left-aligned; every number right-aligned under a
// right-aligned header, in equal columns, without unit words (a row's unit is its stage: POs, or
// re-attempts for Re-attempt); a second line, when a cell has one, right-aligned under its number.
const NUM_COL = 136; // 104px of content + 16px padding each side

// One number cell: the main value, and an optional muted second line under it.
const NumCell: React.FC<{ main: React.ReactNode; sub?: React.ReactNode }> = ({ main, sub }) => (
  <div className={styles.numCell}>
    <div>{main}</div>
    {sub != null ? <div className={styles.numCellSub}>{sub}</div> : null}
  </div>
);

const CompactStagesTable: React.FC<{
  stages: OcOrdersStage[];
  onDrill: (title: string, metric: string) => void;
}> = ({ stages, onDrill }) => {
  const drill = stageDrill(onDrill);
  const num = (v: number | null | undefined, r: OcOrdersStage, part: string, label: string, color?: string) =>
    v == null ? DASH : <MetricLink value={v} count={v} color={color} onClick={drill(r, part, label)} />;
  const eligibleLine = (v: number | null | undefined, r: OcOrdersStage, part: string, label: string) =>
    v == null ? undefined : (
      <>
        <MetricLink value={v} count={v} onClick={drill(r, part, label)} /> eligible
      </>
    );
  const columns: ColumnsType<OcOrdersStage> = [
    {
      title: "Stage",
      dataIndex: "label",
      render: (v: string, r) => (
        <>
          <div className={`${styles.strong} ${styles.nowrap}`}>{v}</div>
          {/* The row's unit beside its owner: the number cells carry no unit words, and the rows differ
              (POs; re-attempts on Re-attempt). */}
          <div className={styles.mutedSmall}>
            {r.owner} · {r.total_unit}
          </div>
        </>
      ),
    },
    {
      title: (
        <HelpHead
          label="Total"
          help="POs (Re-attempt: re-attempts). Under it, the eligible ones: not heavy (over 700 kg) or a large item (over 8 sq ft). Expand the row for the excluded ones by reason."
        />
      ),
      dataIndex: "total",
      align: "right",
      width: NUM_COL,
      render: (v: number | null, r) => (
        <NumCell main={num(v, r, "total", "total")} sub={eligibleLine(r.eligible, r, "eligible", "eligible")} />
      ),
    },
    {
      title: (
        <HelpHead label="Waiting" help="Not done yet: every PO, eligible or not. Under it, the eligible ones." />
      ),
      dataIndex: "waiting",
      align: "right",
      width: NUM_COL,
      render: (v: number | null, r) => (
        <NumCell
          main={num(v, r, "waiting", "waiting", STATUS_META.at_risk_minor.text)}
          sub={eligibleLine(r.eligible_waiting, r, "eligible_waiting", "waiting, eligible")}
        />
      ),
    },
    {
      title: (
        <HelpHead
          label="Done %"
          help="Done out of Eligible where a stage has exclusions (First mile, GRN), with the % of all POs under it; elsewhere done out of Total."
        />
      ),
      key: "done",
      align: "right",
      width: NUM_COL,
      render: (_: unknown, r) =>
        r.eligible_done_pct != null ? (
          <NumCell main={<PctText value={r.eligible_done_pct} />} sub={r.done_pct == null ? DASH : `${r.done_pct}% of all`} />
        ) : (
          <NumCell main={<PctText value={r.done_pct ?? null} />} />
        ),
    },
    ...(stages.some((r) => r.tomorrow != null)
      ? [
          {
            title: "Next day",
            dataIndex: "tomorrow",
            align: "right" as const,
            width: NUM_COL,
            // Like Total and Waiting: every PO due the next day, with its eligible part under it.
            render: (v: number | null | undefined, r: OcOrdersStage) => (
              <NumCell
                main={num(v, r, "tomorrow", "next day", NEXT_DAY_COLOR)}
                sub={eligibleLine(r.eligible_tomorrow, r, "eligible_tomorrow", "next day, eligible")}
              />
            ),
          },
        ]
      : []),
    {
      title: <HelpHead label="Escalations" help="Raised on this stage. Under it, how many are still open." />,
      key: "escalations",
      align: "right",
      width: NUM_COL,
      render: (_: unknown, r) => (
        <NumCell
          main={num(r.escalated, r, "escalated", "escalated")}
          sub={
            r.esc_open ? (
              <MetricLink
                value={`${r.esc_open} open`}
                count={r.esc_open}
                color={STATUS_META.at_risk_major.text}
                onClick={drill(r, "esc_open", "open escalations")}
              />
            ) : undefined
          }
        />
      ),
    },
    {
      title: "Tech errors",
      dataIndex: "tech",
      align: "right",
      width: NUM_COL,
      render: (v: number | null | undefined, r) => (
        <NumCell main={num(v, r, "tech", "tech errors", STATUS_META.breached.text)} />
      ),
    },
    slaColumn(onDrill),
  ];
  return (
    <Table<OcOrdersStage>
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={stages}
      pagination={false}
      className={`${styles.denseTable} ${styles.topAligned}`}
      scroll={{ x: 1300 }}
      expandable={stageExpandable(onDrill)}
    />
  );
};

// The expanded part of a stage row: where waiting units are (Shipment steps) and tech errors by type.
const StageBreakdown: React.FC<{
  stage: OcOrdersStage;
  onDrill: (title: string, metric: string) => void;
}> = ({ stage, onDrill }) => {
  const groups: {
    key: OcStageBreakdown["group"];
    title: string;
    color?: string;
  }[] = [
    { key: "waiting", title: "Waiting, by step" },
    { key: "excluded", title: "Excluded, by reason (still in Total; a PO can have both)" },
    {
      key: "tech",
      title: "Tech errors, by type",
      color: STATUS_META.breached.text,
    },
  ];
  return (
    <div className={styles.stageBreakdown}>
      {groups.map((g) => {
        const items = (stage.breakdown ?? []).filter((b) => b.group === g.key);
        // Excluded: its total first (the reasons can add up to more: a PO can be heavy AND large).
        const excludedTotal = g.key === "excluded" && stage.excluded ? stage.excluded : null;
        if (!items.length && excludedTotal == null) return null;
        return (
          <div key={g.key} className={styles.stageBreakdownGroup}>
            <div className={styles.mutedSmall}>{g.title}</div>
            {excludedTotal != null ? (
              <div className={styles.stageBreakdownItem}>
                <span className={styles.strong}>All excluded</span>
                <MetricLink
                  value={excludedTotal}
                  count={excludedTotal}
                  onClick={() => onDrill(`${stage.label} · excluded`, `stage:${stage.key}:excluded`)}
                />
              </div>
            ) : null}
            {items.map((b) => (
              <div key={b.key} className={styles.stageBreakdownItem}>
                <span>{b.label}</span>
                <MetricLink
                  value={b.count}
                  count={b.count}
                  color={g.color}
                  onClick={() =>
                    onDrill(
                      `${stage.label} · ${b.label}`,
                      `stage:${stage.key}:${b.key}`,
                    )
                  }
                />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export const StagesTable: React.FC<{
  stages: OcOrdersStage[];
  onDrill: (title: string, metric: string) => void;
  // Shipments tab (user, 2026-10-20: too many columns to read): one headline per idea, the rest under
  // it or in the expanded row. Stage + Owner, Total + Eligible (Excluded by reason when expanded),
  // one Done % (eligible big, all small), Escalated + Open esc. as one column. Next day, Tech error
  // and SLA stay as they are.
  compact?: boolean;
}> = ({ stages, onDrill, compact = false }) => {
  if (compact) return <CompactStagesTable stages={stages} onDrill={onDrill} />;
  const drill = stageDrill(onDrill);
  const count =
    (part: "total" | "waiting") => (v: number | null, r: OcOrdersStage) =>
      v == null ? (
        <span className={styles.numUnit}>
          {DASH}
          <span />
        </span>
      ) : (
        <span className={styles.numUnit}>
          <MetricLink
            value={v}
            count={v}
            color={
              part === "waiting" ? STATUS_META.at_risk_minor.text : undefined
            }
            onClick={drill(r, part, part)}
          />
          <span className={styles.unit}>
            {part === "total" ? r.total_unit : r.waiting_unit}
          </span>
        </span>
      );
  const hasDone = stages.some((r) => r.done_pct !== undefined);
  const hasEligible = stages.some((r) => r.eligible != null);
  // Escalation / tech-error counts: clickable, "–" where the stage has none to show.
  const link = (part: string, label: string, color?: string) => ({
    align: "right" as const,
    width: 100,
    render: (v: number | null | undefined, r: OcOrdersStage) =>
      v == null ? (
        DASH
      ) : (
        <MetricLink
          value={v}
          count={v}
          color={color}
          onClick={drill(r, part, label)}
        />
      ),
  });
  const columns: ColumnsType<OcOrdersStage> = [
    {
      title: "Stage",
      dataIndex: "label",
      render: (v: string) => <span className={styles.strong}>{v}</span>,
    },
    {
      title: "Owner",
      dataIndex: "owner",
      render: (v: string) => <span className={styles.muted}>{v}</span>,
    },
    { title: "Total", dataIndex: "total", width: 130, render: count("total") },
    // First mile / GRN only: Total − Excluded = Eligible, read left to right.
    ...(hasEligible
      ? [
          {
            title: (
              <HelpHead
                label="Excluded"
                help="Heavy (over 700 kg) or a large item (over 8 sq ft). Still counted in Total."
              />
            ),
            dataIndex: "excluded",
            ...link("excluded", "excluded (heavy / large item)"),
          },
          {
            title: (
              <HelpHead
                label="Eligible"
                help="Total minus Excluded: the work the team is measured on."
              />
            ),
            dataIndex: "eligible",
            ...link("eligible", "eligible"),
          },
        ]
      : []),
    {
      title: "Waiting",
      dataIndex: "waiting",
      width: 130,
      render: count("waiting"),
    },
    ...(hasDone
      ? [
          {
            title: (
              <HelpHead
                label="Done %"
                help="Done out of Total (Total minus Waiting, divided by Total)."
              />
            ),
            dataIndex: "done_pct",
            align: "right" as const,
            width: 100,
            render: (v: number | null) => <PctText value={v} />,
          },
        ]
      : []),
    ...(hasEligible
      ? [
          {
            title: (
              <HelpHead
                label="Done % of eligible"
                help="Done out of Eligible. Excluded work is not counted against the team."
              />
            ),
            dataIndex: "eligible_done_pct",
            align: "right" as const,
            width: 160,
            render: (v: number | null) => <PctText value={v} />,
          },
        ]
      : []),
    ...(stages.some((r) => r.tomorrow != null)
      ? [
          {
            title: "Next day",
            dataIndex: "tomorrow",
            ...link("tomorrow", "next day", NEXT_DAY_COLOR),
          },
        ]
      : []),
    {
      title: "Escalated",
      dataIndex: "escalated",
      ...link("escalated", "escalated"),
    },
    {
      title: "Open esc.",
      dataIndex: "esc_open",
      ...link("esc_open", "open escalations", STATUS_META.at_risk_major.text),
    },
    {
      title: "Tech error",
      dataIndex: "tech",
      ...link("tech", "tech errors", STATUS_META.breached.text),
    },
    // SLA: one column, a bar + clickable counts (user, 2026-10-07: fewer columns). Own breach /
    // Late · inherited are hidden until their rules exist; Excused sits in the breached detail.
    slaColumn(onDrill),
  ];
  return (
    <Table<OcOrdersStage>
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={stages}
      pagination={false}
      className={styles.denseTable}
      scroll={{ x: 1200 }}
      expandable={stageExpandable(onDrill)}
    />
  );
};
